import type Embed from 'quill/blots/embed';
import Quill, { Delta } from 'quill/core';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuill } from '../editor/QuillContext';

type FaceEditContextType = {
  onEditFacet: ({
    blot,
    prefix,
    separator,
  }: {
    blot: Embed;
    prefix: string;
    separator: string;
  }) => void;
  isEditingFacet: boolean;
};

const FacetEditContext = createContext<FaceEditContextType | null>(null);

export const useFacetEdit = () => {
  const context = useContext(FacetEditContext);
  if (context == null) {
    throw new Error(
      'useFacetEditContext must be used within a FacetEditContext',
    );
  }
  return context;
};

interface FacetEditProviderProps {
  children: React.ReactNode;
}

const FacetEditProvider = ({ children }: FacetEditProviderProps) => {
  const { quill } = useQuill();
  const [isEditingFacet, setIsEditingFacet] = useState(false);
  const contentRef = useRef<Delta | null>();

  useEffect(() => {
    if (!quill) return;

    if (
      !isEditingFacet &&
      contentRef.current &&
      quill.getContents().diff(contentRef.current).length() === 0
    ) {
      quill.history.undo();
      contentRef.current = null;
    }
  }, [isEditingFacet, quill]);

  useEffect(() => {
    if (!quill) return undefined;

    function handleTextChange() {
      contentRef.current = null;
      setIsEditingFacet(false);
    }

    function handleSelectionChange(
      currentRange: Range | null,
      oldRange: Range | null,
    ) {
      if (!currentRange && oldRange) {
        setIsEditingFacet(false);
      }
    }

    quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);
    quill.on(Quill.events.TEXT_CHANGE, handleTextChange);

    return () => {
      quill.off(Quill.events.SELECTION_CHANGE, handleSelectionChange);
      quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
    };
  }, [quill, isEditingFacet]);

  const context = useMemo((): FaceEditContextType => {
    return {
      isEditingFacet,
      onEditFacet({ blot, prefix, separator }) {
        if (!quill) return;

        if (
          isEditingFacet &&
          contentRef.current &&
          quill.getContents().diff(contentRef.current).length() === 0
        ) {
          quill.history.undo();
        }

        const offset = blot.offset();
        if (offset == null) return;
        quill.history.cutoff();
        const trigger = `${prefix}${separator}`;

        quill.updateContents(
          new Delta().retain(offset).delete(1).insert(trigger),
          Quill.sources.USER,
        );

        quill.setSelection({ index: offset + trigger.length, length: 0 });

        contentRef.current = quill.getContents();
        quill.history.cutoff();

        setIsEditingFacet(true);
      },
    };
  }, [quill, isEditingFacet]);

  return (
    <FacetEditContext.Provider value={context}>
      {children}
    </FacetEditContext.Provider>
  );
};

export default FacetEditProvider;
